import { motion, useInView } from "motion/react";
import { useRef } from "react";
import { SparklingStar } from "../ui/SparklingStar";
import geekcowLogo from "../../assets/geekcow-logo-full.png";
import { BRAND } from "../../lib/brand";
import { ABOUT } from "../../data/agencyData";

/**
 * "Who we are" — laid out to match build/WHOWEARESection.svg: eyebrow, the full
 * GeekCow lockup, the three-line coloured statement, the three traits, then the
 * Know more link. Copy and colours come from agencyData and are the design's own.
 */
export function AgencyAbout({ onKnowMore }: { onKnowMore?: () => void }) {
  const ref = useRef<HTMLElement>(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section ref={ref} className="relative w-full py-20 md:py-28 px-4 flex flex-col items-center gap-8">
      {/* Decorative stars, positioned as in the design */}
      <SparklingStar className="left-[22%] top-[18%] w-[20px] h-[20px] hidden md:block" duration={3.5}>
        <svg className="block size-full" fill="none" viewBox="0 0 20 20">
          <path d="M10 0L11.25 8.75L20 10L11.25 11.25L10 20L8.75 11.25L0 10L8.75 8.75L10 0Z" fill={BRAND.ink} />
        </svg>
      </SparklingStar>
      <SparklingStar className="left-[19%] top-[58%] w-[16px] h-[16px] hidden md:block" duration={4} delay={0.9}>
        <svg className="block size-full" fill="none" viewBox="0 0 20 20">
          <path d="M10 0L11.25 8.75L20 10L11.25 11.25L10 20L8.75 11.25L0 10L8.75 8.75L10 0Z" fill={BRAND.ink} />
        </svg>
      </SparklingStar>

      <p className="font-mono text-[12px] md:text-[14px] tracking-[3px] uppercase text-[#666]">{ABOUT.eyebrow}</p>

      <motion.img
        src={geekcowLogo}
        alt="GeekCow Creative Studio"
        // Larger than the design's ~209px: the lockup is the section's anchor.
        className="w-[240px] md:w-[340px] lg:w-[400px] h-auto"
        initial={{ opacity: 0, y: 16 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.5, ease: "easeOut" }}
      />

      <motion.h2
        initial={{ opacity: 0, y: 24 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.6, delay: 0.1, ease: "easeOut" }}
        className="font-bold text-[26px] md:text-[44px] leading-[1.25] text-center max-w-[860px] tracking-tight"
      >
        {ABOUT.statement.map((part, i) => (
          <span key={i} style={{ color: part.color }}>
            {part.text}
          </span>
        ))}
      </motion.h2>

      <div className="flex flex-wrap items-center justify-center gap-8 md:gap-28 lg:gap-36 mt-2">
        {ABOUT.traits.map((trait) => (
          <p key={trait} className="font-black text-[22px] md:text-[26px] leading-[32px] text-[#666]">
            {trait}
          </p>
        ))}
      </div>

      <button
        type="button"
        onClick={onKnowMore}
        className="group flex items-center gap-3 px-4 py-2 rounded-full hover:bg-[#eaf6fb] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#d0e7f5]"
      >
        <span className="font-bold text-[12.6px] leading-[12.6px] tracking-[2.1px] uppercase text-[#003e5b]">
          Know more
        </span>
        <svg
          className="w-[17.5px] h-[12.5px] transition-transform duration-300 group-hover:translate-x-1"
          fill="none"
          viewBox="0 0 17.5209 12.4708"
          aria-hidden="true"
        >
          <path
            d="M17.2814 5.57287C17.6008 6.01455 17.6008 6.45622 17.2814 6.89789L12.6274 12.198C12.2395 12.5617 11.8517 12.5617 11.4639 12.198C11.1445 11.7563 11.1445 11.3146 11.4639 10.873L14.7148 7.17069H0.821293C0.319392 7.11873 0.0456274 6.80696 0 6.23538C0.0456274 5.66381 0.319392 5.35204 0.821293 5.30008H14.7148L11.4639 1.59782C11.1445 1.15614 11.1445 0.714471 11.4639 0.272798C11.8517 -0.0909327 12.2395 -0.0909327 12.6274 0.272798L17.2814 5.57287Z"
            fill={BRAND.navy}
          />
        </svg>
      </button>
    </section>
  );
}
